import { useState } from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import type {
  KnowledgeBuilderFormData,
  KnowledgeBuilderUiOptions,
} from '../../api';
import { useKnowledgeBuilderColors } from '../../theme/knowledgeBuilderColors';
import {
  KnowledgeBuilderProvider,
  useKnowledgeBuilder,
} from './KnowledgeBuilderProvider';
import { KnowledgeBuilderLayout } from './KnowledgeBuilderLayout';
import { DefinitionInterviewProvider } from './DefinitionInterview/DefinitionContext';
import { DefinitionInterviewLayout } from './DefinitionInterview/DefinitionInterviewLayout';

export const KnowledgeBuilderField = (
  props: FieldExtensionComponentProps<
    KnowledgeBuilderFormData,
    KnowledgeBuilderUiOptions
  >,
) => {
  const rootFormData = extractRootFormData(props);
  return (
    <KnowledgeBuilderProvider
      formData={props.formData}
      onChange={props.onChange}
      rootFormData={rootFormData}
      uiOptions={props.uiSchema?.['ui:options'] as KnowledgeBuilderUiOptions}
    >
      <KnowledgeBuilderWorkspace />
    </KnowledgeBuilderProvider>
  );
};

type WorkspaceMode = 'interview' | 'devMode';

const KnowledgeBuilderWorkspace = () => {
  const colors = useKnowledgeBuilderColors();
  const { selectDocument, refreshSessionState } = useKnowledgeBuilder();
  const [mode, setMode] = useState<WorkspaceMode>('interview');
  const [isSyncingMode, setIsSyncingMode] = useState(false);

  const openSource = async (path?: string) => {
    if (path) {
      selectDocument(path);
    }
    await enterDevMode();
  };

  const enterDevMode = async () => {
    setIsSyncingMode(true);
    try {
      // Questionnaire approvals are persisted by the backend. Refresh before
      // mounting the editor so Dev Mode cannot render the pre-approval
      // document snapshot held by the provider or a stale Monaco model.
      await refreshSessionState();
    } finally {
      setIsSyncingMode(false);
      setMode('devMode');
    }
  };

  const toggleMode = () => {
    if (mode === 'interview') {
      void enterDevMode();
      return;
    }
    setMode('devMode');
  };

  const toggleBar = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 6,
        padding: '4px 4px 6px 0',
      }}
    >
      <Typography style={{ fontSize: 12, color: colors.text.secondary }}>
        {mode === 'devMode' ? 'Interview' : 'Dev Mode'}
      </Typography>
      <Switch
        size="small"
        color="primary"
        checked={mode === 'devMode'}
        disabled={isSyncingMode}
        onChange={() => {
          if (mode === 'devMode') {
            setMode('interview');
          } else {
            toggleMode();
          }
        }}
        inputProps={{ 'aria-label': 'Toggle Dev Mode' }}
      />
    </div>
  );

  if (mode === 'devMode') {
    return (
      <>
        {toggleBar}
        <KnowledgeBuilderLayout />
      </>
    );
  }

  return (
    <>
      {toggleBar}
      <div
        className="knowledge-builder-rounded"
        style={{
          border: `1px solid ${colors.border.default}`,
          borderRadius: 12,
          minHeight: 'calc(100vh - 210px)',
          height: 'min(860px, calc(100vh - 210px))',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <DefinitionInterviewProvider>
          <DefinitionInterviewLayout onOpenSource={openSource} />
        </DefinitionInterviewProvider>
      </div>
    </>
  );
};

function extractRootFormData(
  props: FieldExtensionComponentProps<
    KnowledgeBuilderFormData,
    KnowledgeBuilderUiOptions
  >,
) {
  const extendedProps = props as FieldExtensionComponentProps<
    KnowledgeBuilderFormData,
    KnowledgeBuilderUiOptions
  > & {
    formContext?: { formData?: Record<string, unknown> };
    registry?: { formContext?: { formData?: Record<string, unknown> } };
  };

  return (
    extendedProps.formContext?.formData ??
    extendedProps.registry?.formContext?.formData
  );
}
